import { useMemo, useState } from "react"
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import type {
  NameType,
  Payload,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent"
import type { BudgetUpdateReportLine } from "~/ui-rtk/api/budgetUpdateReportsApi"
import { Loader } from "~/ui-rtk/components/ui/common/Loader"
import useDate from "~/ui-rtk/hooks/date"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  type PerformanceEntityKind,
  useEntityPerformance,
} from "../hooks/useEntityPerformance"
import {
  type BeforeAfter,
  beforeAfter,
  changesForEntity,
  dailyMetric,
  formatComparison,
  formatDeltaPair,
  formatPerformance,
  liftHolds,
  type MarginalRead,
  type MarginalVerdict,
  marginalRead,
  MAX_SHORT_READ_DAYS,
  PERFORMANCE_METRICS,
  type PerformanceMetric,
  type RevenueBasis,
  type SameWeekdayReads,
  sameWeekdayReads,
} from "./performance-utils"

/** Chart chrome from the theme: surface, one-step-off gridlines, muted ink. */
const SURFACE = "#101028"
const GRID = "#222446"
const MUTED = "#677489"
const INK = "#F8FAFC"
/** One series: the validated palette's first slot. Changes are marked in the warning tone. */
const SERIES = "#3987e5"
const MARKER = "#c98500"

const CHART_HEIGHT = 380
const WINDOW_OPTIONS = [3, 7, 14] as const

interface EntityOption {
  key: string
  kind: PerformanceEntityKind
  platform: string
  id: string
  label: string
  changes: number
  /** Google branded-search campaign — platform ROAS is not incrementality evidence. */
  brandedSearch: boolean
}

/** Campaigns and ad sets the ledger touched, most-changed first. */
function entityOptions(lines: BudgetUpdateReportLine[]): EntityOption[] {
  const byKey = new Map<string, EntityOption>()
  for (const line of lines) {
    if (line.entity !== "campaign" && line.entity !== "adset") continue
    if (!line.entityId || !line.success || line.metric === "Status") continue
    const key = `${line.platform}|${line.entity}|${line.entityId}`
    const existing = byKey.get(key)
    if (existing) existing.changes += 1
    else {
      byKey.set(key, {
        key,
        kind: line.entity,
        platform: line.platform,
        id: line.entityId,
        label: line.entityName || line.entityId,
        changes: 1,
        brandedSearch: line.brandedSearch,
      })
    }
  }
  return [...byKey.values()].sort(
    (a, b) => b.changes - a.changes || a.label.localeCompare(b.label),
  )
}

function Chips<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
}) {
  return (
    <div className="flex items-center gap-1">
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            "rounded-md px-2 py-1 text-[12px] transition-colors",
            option.value === value
              ? "bg-system-blue-600 font-semibold text-white"
              : "bg-white/5 text-system-gray-400 hover:bg-white/10 hover:text-system-gray-200",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

/** "up $Δ (+N%)" / "down $Δ (-N%)" coloured by direction. */
function DeltaText({
  metric,
  deltaValue,
  deltaPercent,
}: {
  metric: PerformanceMetric
  deltaValue: number | null
  deltaPercent: number | null
}) {
  if (deltaValue === null) {
    return <span className="text-system-gray-500">—</span>
  }
  return (
    <span
      className={cn(
        "tabular-nums",
        deltaValue > 0 && "text-emerald-300",
        deltaValue < 0 && "text-rose-300",
      )}
    >
      {formatDeltaPair(metric, deltaValue, deltaPercent)}
    </span>
  )
}

/**
 * Verdict tone: did the evidence support the change? ADD and CUT HELD
 * (the added dollars paid back / the removed ones were not) read green;
 * PULL BACK and RESTORE (revenue moved against the change) read red; HOLD
 * (paid back, but under 1.00x) amber; FLAT and INSUFFICIENT DATA are no
 * read at all.
 */
const VERDICT_CLASS: Record<MarginalVerdict, string> = {
  ADD: "bg-emerald-500/20 text-emerald-200",
  "CUT HELD": "bg-emerald-500/20 text-emerald-200",
  HOLD: "bg-amber-500/20 text-amber-200",
  "PULL BACK": "bg-rose-500/20 text-rose-200",
  RESTORE: "bg-rose-500/20 text-rose-200",
  FLAT: "bg-white/10 text-system-gray-300",
  "INSUFFICIENT DATA": "bg-white/10 text-system-gray-400",
}

function MarginalCell({ read }: { read: MarginalRead }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={cn(
          "inline-block w-max whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold",
          VERDICT_CLASS[read.verdict],
        )}
      >
        {read.verdict}
      </span>
      <span className="text-[11px] text-system-gray-400">{read.reason}</span>
    </div>
  )
}

/** `liftHolds` result → the strip's wording; null reads "not verified", never a guess. */
const HOLDS_LABEL: Record<string, string> = {
  true: "yes",
  false: "no",
  null: "not verified",
}

/**
 * The short-window read: the after-days vs the same calendar days one and
 * two weeks earlier, each with $ and %, and whether the move holds on all
 * three comparisons (period, 1 week, 2 weeks).
 */
function SameWeekdayCell({
  metric,
  reads,
  periodDelta,
  windowDays,
}: {
  metric: PerformanceMetric
  reads: SameWeekdayReads | null
  periodDelta: number | null
  windowDays: number
}) {
  if (windowDays > MAX_SHORT_READ_DAYS) {
    return (
      <span className="text-[11px] text-system-gray-500">
        n/a — the same-weekday rule is for reads of {MAX_SHORT_READ_DAYS} days
        or fewer
      </span>
    )
  }
  if (!reads) return <span className="text-system-gray-500">no data yet</span>
  const holds = liftHolds([
    periodDelta,
    reads.oneWeekEarlier.deltaValue,
    reads.twoWeeksEarlier.deltaValue,
  ])
  const rows = [reads.oneWeekEarlier, reads.twoWeeksEarlier]
  return (
    <div className="flex flex-col gap-0.5 text-[11px]">
      {rows.map(r => (
        <span key={r.label} className="whitespace-nowrap text-system-gray-300">
          <span className="text-system-gray-500">vs {r.label}:</span>{" "}
          {r.value === null ? (
            <span className="text-system-gray-500">
              no data ({r.from} – {r.to})
            </span>
          ) : (
            <>
              {formatComparison(metric, r.value, reads.read.value)}
              <span className="ml-1 text-system-gray-500">{r.days}d</span>
            </>
          )}
        </span>
      ))}
      <span
        className={cn(
          "font-semibold",
          holds === true && "text-emerald-300",
          holds === false && "text-amber-300",
          holds === null && "text-system-gray-500",
        )}
      >
        holds on all three: {HOLDS_LABEL[String(holds)]}
      </span>
    </div>
  )
}

interface PerformanceChartProps {
  /** Ledger lines of the range (already source-filtered) — they pick the entities. */
  lines: BudgetUpdateReportLine[]
  range: { from: Date; to: Date }
}

/**
 * "Performance around changes": one entity's daily spend / modeled revenue /
 * ROAS from the paid-media cubes, with a vertical marker at every recorded
 * budget or tROAS change, and a before-vs-after strip per change (N days
 * either side, change day excluded, after-window capped at the latest
 * complete day of data). It answers "what happened after we moved it" —
 * not why; seasonality and creative swaps are not controlled for.
 */
export default function PerformanceChart({
  lines,
  range,
}: PerformanceChartProps) {
  const dayjs = useDate()
  const entities = useMemo(() => entityOptions(lines), [lines])
  const [pickedKey, setPickedKey] = useState<string | null>(null)
  const entity = entities.find(e => e.key === pickedKey) ?? entities[0] ?? null
  const [metric, setMetric] = useState<PerformanceMetric>("DR ROAS")
  const [windowDays, setWindowDays] =
    useState<(typeof WINDOW_OPTIONS)[number]>(7)

  const { days, latestCompleteDate, isLoading, error } = useEntityPerformance(
    entity?.kind ?? "campaign",
    entity?.platform ?? null,
    entity?.id ?? null,
    range,
  )

  const changes = useMemo(
    () =>
      entity
        ? changesForEntity(lines, entity.key, iso =>
            dayjs(iso).format("YYYY-MM-DD"),
          )
        : [],
    [lines, entity, dayjs],
  )

  const points = useMemo(
    () =>
      days.map(d => ({
        t: dayjs(d.date).valueOf(),
        date: d.date,
        value: dailyMetric(d, metric),
      })),
    [days, metric, dayjs],
  )

  const comparisons = useMemo<BeforeAfter[]>(
    () =>
      changes.map(change =>
        beforeAfter(days, change, metric, windowDays, latestCompleteDate),
      ),
    [changes, days, metric, windowDays, latestCompleteDate],
  )

  // The marginal read divides by the revenue the picked metric is judged on:
  // brand value on the Brand ROAS basis, modeled revenue otherwise.
  const basis: RevenueBasis =
    metric === "Brand ROAS" ? "brandValue" : "modeledRevenue"
  const marginals = useMemo<MarginalRead[]>(
    () =>
      changes.map(change =>
        marginalRead(days, change, windowDays, latestCompleteDate, basis),
      ),
    [changes, days, windowDays, latestCompleteDate, basis],
  )
  const weekdayReads = useMemo<Array<SameWeekdayReads | null>>(
    () =>
      changes.map(change =>
        sameWeekdayReads(days, change, metric, windowDays, latestCompleteDate),
      ),
    [changes, days, metric, windowDays, latestCompleteDate],
  )

  const formatDate = (t: number) => dayjs(t).format("MMM D, YYYY")

  if (entities.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-basic-blue text-sm text-system-gray-500"
        style={{ height: CHART_HEIGHT }}
      >
        No campaigns or ad sets with applied changes in this range.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[11px] text-system-gray-500">
        Daily performance from the data pipeline for one entity, with each
        recorded change marked. Before / after compares the {windowDays} days
        either side of a change (change day excluded), with the $ and the %
        move. The marginal read asks whether the dollars the change added (or
        removed) paid back at the margin — Δ revenue ÷ Δ spend per day, on this
        entity&apos;s own modeled revenue (brand value on the Brand ROAS basis),
        not the brand-level new-customer revenue — and fails closed to
        INSUFFICIENT DATA under 3 complete days. It shows what followed a
        change, not what caused it.
      </p>
      {entity?.brandedSearch && (
        <p className="rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-[12px] text-violet-100">
          <span className="font-semibold">Branded search.</span> Platform and
          modeled ROAS are not incrementality evidence for brand terms: the rule
          is Δ spend ÷ Δ total (paid + organic) branded clicks against the value
          per click, scaled on the marginal cost, never the blended average.
          Read a brand-spend change on branded search conversion rate and total
          branded clicks, not on this chart alone.
        </p>
      )}

      {/* Entity picker: one at a time — a single line keeps the y-axis honest. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {entities.map(e => (
          <button
            key={e.key}
            type="button"
            onClick={() => setPickedKey(e.key)}
            title={`${e.platform} · ${e.kind} · ${e.id}`}
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px] transition-colors",
              entity?.key === e.key
                ? "border-white/20 bg-white/10 text-white"
                : "border-transparent bg-white/5 text-system-gray-400 hover:bg-white/10 hover:text-system-gray-200",
            )}
          >
            <span className="max-w-[14rem] truncate">{e.label}</span>
            <span className="rounded bg-white/10 px-1 text-[10px] tabular-nums text-system-gray-400">
              {e.changes}
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Chips<PerformanceMetric>
          value={metric}
          onChange={setMetric}
          options={PERFORMANCE_METRICS.map(m => ({ value: m, label: m }))}
        />
        <div className="flex items-center gap-2 text-[11px] text-system-gray-500">
          Window
          <Chips<string>
            value={String(windowDays)}
            onChange={v =>
              setWindowDays(Number(v) as (typeof WINDOW_OPTIONS)[number])
            }
            options={WINDOW_OPTIONS.map(d => ({
              value: String(d),
              label: `${d}d`,
            }))}
          />
        </div>
      </div>

      {error && (
        <p className="text-sm text-rose-300">
          Couldn&apos;t load performance data for this entity.
        </p>
      )}

      <div
        className="relative rounded-lg border border-basic-blue p-2"
        style={{ backgroundColor: SURFACE, height: CHART_HEIGHT }}
      >
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-basic-black/60 text-white">
            <Loader size="large" />
          </div>
        )}
        {!isLoading && points.length === 0 && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-system-gray-500">
            No daily data for this entity in the range.
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={points}
            margin={{ top: 28, right: 24, bottom: 4, left: 4 }}
          >
            <CartesianGrid stroke={GRID} strokeWidth={1} vertical={false} />
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={[range.from.getTime(), range.to.getTime()]}
              tickFormatter={t => dayjs(t).format("MMM D")}
              tick={{ fill: MUTED, fontSize: 11 }}
              axisLine={{ stroke: GRID }}
              tickLine={false}
              minTickGap={32}
            />
            <YAxis
              domain={[0, "auto"]}
              tickFormatter={v => formatPerformance(metric, Number(v), true)}
              tick={{ fill: MUTED, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={72}
            />
            <Tooltip
              cursor={{ stroke: MUTED, strokeWidth: 1 }}
              animationDuration={0}
              content={({ active, payload, label }) => {
                const entry = (payload as Payload<ValueType, NameType>[])?.[0]
                if (!active || !entry || typeof label !== "number") return null
                const changeHere = changes.filter(
                  c => c.date === dayjs(label).format("YYYY-MM-DD"),
                )
                return (
                  <div className="rounded-lg border border-basic-blue bg-basic-dark-blue/95 px-3 py-2 text-left shadow-xl">
                    <div className="mb-1 text-[11px] text-system-gray-400">
                      {formatDate(label)}
                    </div>
                    <div className="text-[13px] font-semibold text-white">
                      {formatPerformance(
                        metric,
                        typeof entry.value === "number" ? entry.value : null,
                      )}{" "}
                      <span className="font-normal text-system-gray-400">
                        {metric}
                      </span>
                    </div>
                    {changeHere.map(c => (
                      <div
                        key={c.id}
                        className="mt-1 text-[11px]"
                        style={{ color: MARKER }}
                      >
                        {c.label} · {c.source}
                      </div>
                    ))}
                  </div>
                )
              }}
            />
            {changes.map((c, index) => (
              <ReferenceLine
                key={c.id}
                x={dayjs(c.date).valueOf()}
                stroke={MARKER}
                strokeDasharray="4 3"
                label={{
                  value: c.label,
                  position: "top",
                  fill: INK,
                  fontSize: 10,
                  // Alternate two rows so neighbouring labels don't collide.
                  dy: index % 2 === 0 ? -2 : 12,
                }}
              />
            ))}
            <Line
              type="monotone"
              name={metric}
              dataKey="value"
              stroke={SERIES}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              dot={false}
              activeDot={{
                r: 5,
                stroke: SURFACE,
                strokeWidth: 2,
                fill: SERIES,
              }}
              connectNulls={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Before / after per change */}
      <div className="overflow-auto rounded-lg border border-basic-blue">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {[
                "Change",
                "When",
                `Before (${windowDays}d)`,
                `After (${windowDays}d)`,
                "Delta",
                "Marginal read",
                "Same weekdays",
                "Source",
              ].map(header => (
                <th
                  key={header}
                  className="border-b border-basic-blue bg-system-blue-800 px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-system-gray-400"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comparisons.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-2 py-1 text-[12px] text-system-gray-500"
                >
                  No applied changes for this entity in the range.
                </td>
              </tr>
            )}
            {comparisons.map((c, index) => (
              <tr key={c.change.id} className="hover:bg-white/5">
                <td className="border-b border-white/5 px-2 py-1 text-[12px] text-system-gray-100">
                  <div className="flex flex-col gap-0.5">
                    <span>{c.change.label}</span>
                    {c.change.magnitude === "big" && (
                      <span
                        className="w-max rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-200"
                        title="≥ 20% or ≥ $250/day — must clear the total-spend gate (and, on branded search, the incrementality read)"
                      >
                        Big change
                      </span>
                    )}
                  </div>
                </td>
                <td className="border-b border-white/5 px-2 py-1 text-[12px] text-system-gray-300 whitespace-nowrap">
                  {dayjs(c.change.changedAt).format("MMM D, YYYY HH:mm")}
                </td>
                <td className="border-b border-white/5 px-2 py-1 text-right text-[12px] tabular-nums text-system-gray-100">
                  {formatPerformance(metric, c.before.value)}
                  <span className="ml-1 text-[10px] text-system-gray-500">
                    {c.before.days}d
                  </span>
                </td>
                <td className="border-b border-white/5 px-2 py-1 text-right text-[12px] tabular-nums text-system-gray-100">
                  {c.after.days === 0 ? (
                    <span className="text-system-gray-500">no data yet</span>
                  ) : (
                    <>
                      {formatPerformance(metric, c.after.value)}
                      <span
                        className={cn(
                          "ml-1 text-[10px]",
                          c.afterTruncated
                            ? "text-amber-300"
                            : "text-system-gray-500",
                        )}
                        title={
                          c.afterTruncated
                            ? "Window cut short by the latest complete day of data"
                            : undefined
                        }
                      >
                        {c.after.days}d{c.afterTruncated ? " (partial)" : ""}
                      </span>
                    </>
                  )}
                </td>
                <td className="border-b border-white/5 px-2 py-1 text-right text-[12px] whitespace-nowrap">
                  <DeltaText
                    metric={metric}
                    deltaValue={c.deltaValue}
                    deltaPercent={c.deltaPercent}
                  />
                </td>
                <td className="border-b border-white/5 px-2 py-1 text-[12px]">
                  <MarginalCell read={marginals[index]} />
                </td>
                <td className="border-b border-white/5 px-2 py-1 text-[12px]">
                  <SameWeekdayCell
                    metric={metric}
                    reads={weekdayReads[index]}
                    periodDelta={c.deltaValue}
                    windowDays={windowDays}
                  />
                </td>
                <td className="border-b border-white/5 px-2 py-1 text-[12px] text-system-gray-400">
                  {c.change.source} · {c.change.changedBy}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
